import { productTaxonomyRouteCatalog } from './renames';
import type { ProductTaxonomyRouteEntry, ProductTaxonomyRouteTemplate, ProductTaxonomyStage } from './types';

export type { ProductTaxonomyRouteTemplate } from './types';

export type ProductTaxonomyRouteKey = keyof typeof productTaxonomyRouteCatalog;

export function resolveProductTaxonomyRoute(
  key: ProductTaxonomyRouteKey,
  stage: ProductTaxonomyStage,
): ProductTaxonomyRouteTemplate {
  // The `as const` catalog narrows entries to their literal shapes, which drops the
  // optional `final` override; read through the declared entry type to recover it.
  const entry: ProductTaxonomyRouteEntry = productTaxonomyRouteCatalog[key];

  if (stage === 'final') {
    return entry.final ?? entry.future;
  }

  return entry[stage];
}

// Every catalog template starts with this prefix (enforced by
// `ProductTaxonomyRouteTemplate`). Fragments are taken from just past it, because the
// `:workspaceId` param never appears literally in a live pathname while everything
// after it does.
const TAXONOMY_ROUTE_PREFIX = '/workspaces/:workspaceId';

// Renamed→current path-fragment replacements derived from the route catalog. Longest
// fragments run first (so nested renames replace before their prefixes) and we dedupe by
// renamed fragment so the handful of many-to-one renames resolve deterministically. Both
// the `future` and the `final` template are collected, because a rename the end state
// withdraws (People) serves a different segment per stage, and every stage's spelling has
// to canonicalize.
//
// Fragments are sliced off the shared prefix rather than off `/journeys`: most renames sit
// under `/journeys/...`, but the migrated Hydra pages (e.g. `/analysis/deliverability` →
// `/reports/deliverability`) have no `/journeys` segment at all, and slicing at a missing
// substring would silently yield a one-character fragment.
const JOURNEYS_PATHNAME_CANONICALIZATIONS: { renamed: string; current: string }[] = (() => {
  const seenRenamedFragments = new Set<string>();
  const replacements: { renamed: string; current: string }[] = [];

  for (const entry of Object.values<ProductTaxonomyRouteEntry>(productTaxonomyRouteCatalog)) {
    const currentFragment = entry.current.slice(TAXONOMY_ROUTE_PREFIX.length);

    for (const template of [entry.future, entry.final]) {
      if (template == null) {
        continue;
      }

      const renamedFragment = template.slice(TAXONOMY_ROUTE_PREFIX.length);

      if (renamedFragment === currentFragment || seenRenamedFragments.has(renamedFragment)) {
        continue;
      }

      seenRenamedFragments.add(renamedFragment);
      replacements.push({ renamed: renamedFragment, current: currentFragment });
    }
  }

  return replacements.sort((a, b) => b.renamed.length - a.renamed.length);
})();

/**
 * Canonicalize a pathname to the current (pre-rename) vocabulary.
 *
 * Under the product-taxonomy rename flags, Journeys routes are served at their
 * renamed paths (e.g. `/journeys/automations`, `/journeys/profiles`,
 * `/reports/deliverability`). Route patterns across the app are written against the
 * current paths (`/journeys/campaigns`, `/journeys/people`,
 * `/analysis/deliverability`), so live pathnames are normalized here before
 * matching. Driven by the route catalog, so every rename — present and future,
 * `/journeys`-prefixed or not — is covered without per-rename code.
 */
export function normalizeJourneysPathname(pathname: string): string {
  for (const { renamed, current } of JOURNEYS_PATHNAME_CANONICALIZATIONS) {
    const index = pathname.indexOf(renamed);

    if (index === -1) {
      continue;
    }

    // Only replace at a segment boundary (end of path or a following `/`), so
    // e.g. `/journeys/reports` never matches inside `/journeys/reports-archive`.
    const nextChar = pathname[index + renamed.length];
    if (nextChar === undefined || nextChar === '/') {
      return pathname.slice(0, index) + current + pathname.slice(index + renamed.length);
    }
  }

  return pathname;
}
